import { pWarn } from '_/helpers/logger';
import { eventBus } from '_/helpers/eventBus';
import { mapGetters } from 'vuex';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-json';
import debounce from 'lodash/debounce';

export default {
    name: 'JsonField',
    
    /**
     * Define props
     *
     * @type  {Object}
     */
    props: {
        defaultValue: {
            type: [String, Object, Array],
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        id: {
            required: true,
            type: [Number, String],
        },
        name: {
            required: true,
            type: String,
        },
        placeholder: {
            type: String,
            default: 'Enter JSON data...',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        refreshFields: {
            type: Number,
        },        
        value: {
            type: [String, Object, Array],
            default: null,
        },
        focus: {
            default: false,
            type: Boolean,
        },
    },

    /**
     * Set the data property
     *
     * @return  {object}
     */
    data() {
        return {
            jsonString: '',
            jsonError: null,
            // Cache for memoized highlighting
            highlightCache: new Map(),
            // Debounced version of onChange
            debouncedOnChange: null
        };
    },
    
    /**
     * Set the computed properties
     *
     * @type  {Object}
     */
    computed: Object.assign({
        spellcheck() {
            return this.ui ? this.ui.photonConfig.spellcheck : false;
        },

        hasValue() {
            return this.jsonString && this.jsonString.trim() !== '';
        },
    }, mapGetters({
        ui: 'ui/ui',
    })),

    /**
     * Set the methods
     *
     * @type  {Object}
     */
    methods: {
        /**
         * Creates the event payload
         *
         * @param   {object}  event
         * @return  {object}
         */
        createEventPayload(event) {
            // Follow exactly the same structure as InputTag.js
            if (event && event.target) {
                return {
                    event,
                    id: event.target.id,
                    name: event.target.name,
                    value: event.target.value,
                };
            } else if (typeof event === 'string') {
                // Handle string input (special case for JSON component)
                return {
                    id: this.id,
                    name: this.name,
                    value: event,
                };
            } else {
                // Handle direct value input (special case for JSON component)
                const valueToUse = typeof event === 'object' && event !== null ? 
                    JSON.stringify(event) : event;
                    
                return {
                    id: this.id,
                    name: this.name,
                    value: valueToUse,
                };
            }
        },

        /**
         * Emits a change event with a payload consisting of input value and the event object
         *
         * @param   {event|string}  input - Either an event object or a string value
         * @return  {void}
         */
        /**
         * Handle input changes with debouncing
         * @param {string|Event} input - The input value or event
         */
        onChange(input) {
            const value = typeof input === 'string' ? input : 
                (input && input.target ? input.target.value : input);
            
            // Update the local state immediately for responsive UI
            this.jsonString = value;
            
            // Use debounced version for the heavy operations
            this.debouncedOnChange(value);
        },
        
        /**
         * Handle the actual change with debouncing
         * @param {string} value - The value to process
         */
        handleChange(value) {
            // Validate JSON first
            if (!this.validateJson(value)) {
                return; // Don't emit if invalid
            }
            
            // Create a synthetic event that exactly matches what InputTag expects
            const syntheticEvent = {
                target: {
                    id: this.id,
                    name: this.name,
                    value: value
                }
            };
            
            const payload = this.createEventPayload(syntheticEvent);
            this.$emit('change', payload);
            eventBus.$emit('fieldTypeChange', payload);
        },
        
        /**
         * Validate JSON and set error state
         * @param {string} value - The JSON string to validate
         * @return {boolean} True if valid, false otherwise
         */
        validateJson(value) {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                this.jsonError = null;
                return true;
            }
            
            try {
                JSON.parse(value);
                this.jsonError = null;
                return true;
            } catch (e) {
                this.jsonError = 'Invalid JSON: ' + e.message;
                return false;
            }
        }, 

        /**
         * Emits a global blur event with a payload consisting of input value and the event object
         *
         * @param   {event}  event
         * @return  {void}
         */
        onBlur(event) {
            eventBus.$emit('fieldTypeBlur', this.createEventPayload(event));
        },
        
        /**
         * Updates the editor from a value
         * 
         * @param {any} value
         * @return {void}
         */
        updateFromValue(value) {
            try {
                if (typeof value === 'string') {
                    this.jsonString = value ? value : '';
                } else {
                    this.jsonString = value ? JSON.stringify(value, null, 2) : '';
                }
                this.jsonError = null;
            } catch (e) {
                console.error('Error parsing JSON:', e);
                this.jsonError = 'Invalid JSON: ' + e.message;
                this.jsonString = typeof value === 'string' ? value : '';
            }
        },

        /**
         * Sets a component value to defaultValue, if required
         *
         * @return  {void}
         */
        setDefaultValueIfRequired() {
            if (this.value === null && this.defaultValue) {
                this.updateFromValue(this.defaultValue);
                
                // This matches exactly what InputTag.js does
                this.$emit('change', {
                    id: this.id,
                    name: this.name,
                    value: this.jsonString,
                });
            }
        },

        /**
         * Syntax highlighter for JSON
         * 
         * @param {string} code
         * @return {string}
         */
        /**
         * Memoized syntax highlighter for JSON
         * @param {string} code - The code to highlight
         * @return {string} Highlighted HTML
         */
        highlighter(code) {
            // Return cached result if available
            if (this.highlightCache.has(code)) {
                return this.highlightCache.get(code);
            }
            
            // Generate and cache the result
            const highlighted = highlight(code, languages.json, 'json');
            this.highlightCache.set(code, highlighted);
            
            // Limit cache size to prevent memory leaks
            if (this.highlightCache.size > 100) {
                // Remove the oldest entry
                const firstKey = this.highlightCache.keys().next().value;
                this.highlightCache.delete(firstKey);
            }
            
            return highlighted;
        },
        
        /**
         * Validates the field
         *
         * @return  {boolean}
         */
        validate() {
            if (this.required && (!this.jsonString || this.jsonString.trim() === '')) {
                this.jsonError = 'This field is required';
                return false;
            }

            if (this.jsonString && this.jsonString.trim() !== '') {
                try {
                    JSON.parse(this.jsonString);
                } catch (e) {
                    this.jsonError = 'Invalid JSON: ' + e.message;
                    return false;
                }
            }

            this.jsonError = null;
            return true;
        },
    },

    /**
     * Set the mounted hook
     *
     * @return  {void}
     */
    created() {
        // Initialize debounced version of onChange with 300ms delay
        this.debouncedOnChange = debounce(this.handleChange, 300);
    },

    mounted() {
        this.$nextTick(() => {
            this.setDefaultValueIfRequired();
            this.updateFromValue(this.value);

            if (this.focus) {
                $(this.$el).find(`#${this.id}`).focus();
            }
        });
    },
    
    beforeDestroy() {
        // Cancel any pending debounced calls when component is destroyed
        if (this.debouncedOnChange && this.debouncedOnChange.cancel) {
            this.debouncedOnChange.cancel();
        }
    }, 

    /**
     * Set the watch properties
     *
     * @type  {Object}
     */
    watch: {
        refreshFields(newEntry, oldEntry) {
            if (newEntry !== oldEntry) {
                pWarn('Watched property fired.', `Json.${this.name}`, 'refreshFields', newEntry, oldEntry, this);

                this.setDefaultValueIfRequired();
                this.updateFromValue(this.value);
                
                this.$forceUpdate();

                $(this.$el).val(this.jsonString);

                if (this.focus) {
                    $(this.$el).find(`#${this.id}`).focus();
                }
            }
        },
        
        value(newVal) {
            if (newVal !== undefined) {
                this.updateFromValue(newVal);
            }
        },
    },
};
